import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatCustomerId, formatServerId } from '../utils/formatId';
import toast from 'react-hot-toast';
import {
  Search, Users, Server, MessageSquare, ChevronRight,
  Play, Square, AlertTriangle, CheckCircle, StickyNote,
  Shield, UserCheck, Loader, RefreshCw, Eye, Hash,
  DollarSign, Clock, Lock, Unlock
} from 'lucide-react';

// ─── CUSTOMER SEARCH ──────────────────────────────────────────
const CustomerSearch = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/staff/customers', { params: { search: q } });
      setResults(data.customers);
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <div>
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or #ID..."
          className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
        />
        {loading && <Loader className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />}
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(c)}
              className="w-full flex items-center justify-between bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl px-4 py-3 transition-colors group text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-green-400 text-xs font-bold">
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-white">{c.first_name} {c.last_name}</div>
                  <div className="text-gray-400 text-xs">{c.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="bg-gray-900 px-2 py-1 rounded-lg font-mono">{formatCustomerId(c.customer_number)}</span>
                {c.server_count > 0 && <span>{c.server_count} servers</span>}
                {c.open_tickets > 0 && <span className="text-yellow-400">{c.open_tickets} tickets</span>}
                <ChevronRight className="w-4 h-4 group-hover:text-white transition-colors" />
              </div>
            </button>
          ))}
        </div>
      )}

      {query && !loading && results.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-8">No customers found for "{query}"</p>
      )}
    </div>
  );
};

// ─── CUSTOMER PROFILE ─────────────────────────────────────────
const CustomerProfile = ({ customerId, onBack, isAdmin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('servers');
  const [roleChanging, setRoleChanging] = useState(false);

  useEffect(() => {
    api.get(`/staff/customers/${customerId}`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoading(false));
  }, [customerId]);

  const changeRole = async (role) => {
    if (!window.confirm(`Change role to "${role}"?`)) return;
    setRoleChanging(true);
    try {
      await api.patch(`/staff/users/${customerId}/role`, { role });
      setData((prev) => ({ ...prev, customer: { ...prev.customer, role } }));
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role');
    } finally {
      setRoleChanging(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader className="w-8 h-8 text-green-400 animate-spin" /></div>;
  if (!data) return null;

  const { customer, servers, orders, tickets, payments } = data;
  const totalSpent = payments.filter(p => p.status === 'completed').reduce((s, p) => s + parseFloat(p.amount), 0);

  const TABS = [
    { key: 'servers', label: 'Servers', count: servers.length },
    { key: 'tickets', label: 'Tickets', count: tickets.filter(t => t.status === 'open').length },
    { key: 'billing', label: 'Billing', count: orders.length },
  ];

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors">
        ← Back to search
      </button>

      {/* Customer header */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-green-500/10 rounded-2xl flex items-center justify-center text-xl font-black text-green-400">
              {customer.first_name?.[0]}{customer.last_name?.[0]}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-black text-white">{customer.first_name} {customer.last_name}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  customer.role === 'admin' ? 'bg-purple-500/10 text-purple-400' :
                  customer.role === 'support_agent' ? 'bg-blue-500/10 text-blue-400' :
                  'bg-gray-700 text-gray-400'
                }`}>{customer.role}</span>
              </div>
              <div className="text-gray-400 text-sm">{customer.email}</div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{formatCustomerId(customer.customer_number)}</span>
                <span>·</span>
                <span>Joined {new Date(customer.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-4 text-center">
            <div className="bg-gray-800 rounded-xl px-4 py-2.5">
              <div className="text-white font-bold">{servers.length}</div>
              <div className="text-gray-500 text-xs">Servers</div>
            </div>
            <div className="bg-gray-800 rounded-xl px-4 py-2.5">
              <div className="text-green-400 font-bold">€{totalSpent.toFixed(2)}</div>
              <div className="text-gray-500 text-xs">Spent</div>
            </div>
            <div className="bg-gray-800 rounded-xl px-4 py-2.5">
              <div className="text-white font-bold">{tickets.filter(t => t.status === 'open').length}</div>
              <div className="text-gray-500 text-xs">Open tickets</div>
            </div>
          </div>
        </div>

        {/* Role management — admin only */}
        {isAdmin && (
          <div className="mt-4 pt-4 border-t border-gray-800 flex items-center gap-3">
            <span className="text-gray-500 text-sm">Role:</span>
            {['customer', 'support_agent', 'admin'].map((role) => (
              <button
                key={role}
                onClick={() => customer.role !== role && changeRole(role)}
                disabled={roleChanging || customer.role === role}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  customer.role === role
                    ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-400 border border-gray-700'
                }`}
              >
                {role === 'support_agent' ? 'Support Agent' : role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
            {roleChanging && <Loader className="w-4 h-4 text-gray-500 animate-spin" />}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
        {TABS.map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === key ? 'bg-green-500 text-gray-950' : 'text-gray-400 hover:text-white'
            }`}
          >
            {label}
            {count > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === key ? 'bg-gray-950/30 text-gray-950' : 'bg-gray-700 text-gray-400'}`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Servers tab */}
      {tab === 'servers' && (
        <div className="space-y-3">
          {servers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No servers</div>
          ) : servers.map((server) => (
            <ServerCard key={server.id} server={server} onAction={() => {
              api.get(`/staff/customers/${customerId}`).then(({ data: d }) => setData(d));
            }} />
          ))}
        </div>
      )}

      {/* Tickets tab */}
      {tab === 'tickets' && (
        <div className="space-y-2">
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No tickets</div>
          ) : tickets.map((ticket) => (
            <Link
              key={ticket.id}
              to={`/tickets/${ticket.id}`}
              className="flex items-center justify-between bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl px-4 py-3 transition-colors"
            >
              <div>
                <div className="font-medium text-white">{ticket.subject}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  #{ticket.id.slice(0, 8).toUpperCase()} · {ticket.category} · {new Date(ticket.updated_at).toLocaleDateString()}
                </div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                ticket.status === 'open' ? 'bg-green-500/10 text-green-400' : 'bg-gray-700 text-gray-400'
              }`}>{ticket.status}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Billing tab */}
      {tab === 'billing' && (
        <div className="space-y-3">
          {payments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No payments</div>
          ) : payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-xl px-4 py-3">
              <div>
                <div className="font-medium text-white">{p.plan_name} Plan</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  via {p.provider} · {new Date(p.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-white font-bold">€{parseFloat(p.amount).toFixed(2)}</div>
                <span className={`text-xs ${p.status === 'completed' ? 'text-green-400' : 'text-red-400'}`}>{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── SERVER CARD WITH STAFF ACTIONS ───────────────────────────
const ServerCard = ({ server, onAction }) => {
  const [acting, setActing] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState([]);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');

  const STATUS_COLOR = {
    running: 'text-green-400', stopped: 'text-gray-400',
    suspended: 'text-red-400', installing: 'text-yellow-400',
  };

  const doAction = async (action) => {
    if (action === 'suspend' && !window.confirm(`Suspend server "${server.name}"? The customer will lose access immediately.`)) return;
    setActing(true);
    try {
      await api.post(`/staff/servers/${server.id}/action`, { action, reason });
      toast.success(`Server ${action} successful`);
      setReason('');
      onAction();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const loadLog = async () => {
    try {
      const { data } = await api.get(`/staff/servers/${server.id}/log`);
      setLog(data.log);
      setShowLog(true);
    } catch {
      toast.error('Failed to load log');
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-500/10 rounded-xl flex items-center justify-center">
            <Server className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">{server.name}</span>
              <span className="text-xs font-mono bg-gray-800 text-gray-400 px-2 py-0.5 rounded-lg">{formatServerId(server.server_number)}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{server.plan_name} · {server.server_type} {server.minecraft_version}</div>
          </div>
        </div>
        <span className={`text-xs font-medium capitalize ${STATUS_COLOR[server.status] || 'text-gray-400'}`}>
          ● {server.status}
        </span>
      </div>

      {/* Reason input */}
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for action (optional, logged)"
        className="w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-green-500 mb-3 transition-colors"
      />

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => doAction('start')}
          disabled={acting || server.status === 'running'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 text-xs rounded-lg transition-colors disabled:opacity-30"
        >
          {acting ? <Loader className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Start
        </button>
        <button
          onClick={() => doAction('stop')}
          disabled={acting || server.status === 'stopped'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-xs rounded-lg transition-colors disabled:opacity-30"
        >
          <Square className="w-3 h-3" /> Stop
        </button>
        <button
          onClick={() => doAction('restart')}
          disabled={acting}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/20 text-yellow-400 text-xs rounded-lg transition-colors disabled:opacity-30"
        >
          <RefreshCw className="w-3 h-3" /> Restart
        </button>
        {server.status !== 'suspended' ? (
          <button
            onClick={() => doAction('suspend')}
            disabled={acting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 text-orange-400 text-xs rounded-lg transition-colors disabled:opacity-30"
          >
            <Lock className="w-3 h-3" /> Suspend
          </button>
        ) : (
          <button
            onClick={() => doAction('unsuspend')}
            disabled={acting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-400 text-xs rounded-lg transition-colors disabled:opacity-30"
          >
            <Unlock className="w-3 h-3" /> Unsuspend
          </button>
        )}
        <button
          onClick={loadLog}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 text-xs rounded-lg transition-colors ml-auto"
        >
          <Eye className="w-3 h-3" /> Action Log
        </button>
        <Link
          to={`/console/${server.id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 text-xs rounded-lg transition-colors"
        >
          Console →
        </Link>
      </div>

      {/* Action log */}
      {showLog && (
        <div className="mt-4 pt-4 border-t border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Action Log</span>
            <button onClick={() => setShowLog(false)} className="text-gray-600 hover:text-gray-400 text-xs">hide</button>
          </div>
          {log.length === 0 ? (
            <p className="text-gray-600 text-xs">No actions logged yet</p>
          ) : (
            <div className="space-y-1.5">
              {log.map((entry) => (
                <div key={entry.id} className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="text-gray-300 font-medium capitalize">{entry.action}</span>
                  <span>by {entry.first_name} {entry.last_name}</span>
                  {entry.details && <span>— {entry.details}</span>}
                  <span className="ml-auto">{new Date(entry.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── SERVER LIST TAB ──────────────────────────────────────────
const ServerList = () => {
  const [servers, setServers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/staff/servers', { params: { search, status: statusFilter } });
      setServers(data.servers);
    } catch {
      toast.error('Failed to load servers');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const STATUS_BADGE = {
    running: 'bg-green-500/10 text-green-400', stopped: 'bg-gray-700 text-gray-400',
    suspended: 'bg-red-500/10 text-red-400', installing: 'bg-yellow-500/10 text-yellow-400',
  };

  return (
    <div>
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search servers by name, email or #ID..."
            className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 transition-colors"
        >
          <option value="">All statuses</option>
          <option value="running">Running</option>
          <option value="stopped">Stopped</option>
          <option value="suspended">Suspended</option>
          <option value="installing">Installing</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-medium">#</th>
                <th className="text-left px-5 py-3 font-medium">Server</th>
                <th className="text-left px-5 py-3 font-medium">Customer</th>
                <th className="text-left px-5 py-3 font-medium">Plan</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {servers.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">No servers found</td></tr>
              ) : servers.map((s) => (
                <tr key={s.id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-gray-500">{formatServerId(s.server_number)}</td>
                  <td className="px-5 py-3 font-medium text-white">{s.name}</td>
                  <td className="px-5 py-3">
                    <div className="text-gray-300">{s.first_name} {s.last_name}</div>
                    <div className="text-gray-500 text-xs font-mono">{formatCustomerId(s.customer_number)}</div>
                  </td>
                  <td className="px-5 py-3 text-gray-400">{s.plan_name}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[s.status] || 'bg-gray-700 text-gray-400'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <Link to={`/console/${s.id}`} className="text-green-400 hover:text-green-300 text-xs transition-colors">
                      Console →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── AGENTS TAB (admin only) ──────────────────────────────────
const AgentsTab = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/staff/agents').then(({ data }) => setAgents(data.agents)).finally(() => setLoading(false));
  }, []);

  const ROLE_STYLE = {
    admin: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    support_agent: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };

  return (
    <div className="space-y-3">
      {loading ? (
        <div className="flex justify-center py-12"><Loader className="w-6 h-6 text-green-400 animate-spin" /></div>
      ) : agents.map((agent) => (
        <div key={agent.id} className="flex items-center justify-between bg-gray-900 border border-gray-800 rounded-xl px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-purple-500/10 rounded-xl flex items-center justify-center text-xs font-bold text-purple-400">
              {agent.first_name?.[0]}{agent.last_name?.[0]}
            </div>
            <div>
              <div className="font-semibold text-white">{agent.first_name} {agent.last_name}</div>
              <div className="text-gray-500 text-xs">{agent.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-gray-500">{formatCustomerId(agent.customer_number)}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_STYLE[agent.role]}`}>
              {agent.role === 'support_agent' ? 'Support Agent' : 'Admin'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── MAIN STAFF PAGE ──────────────────────────────────────────
const StaffPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('customers');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const isAdmin = user?.role === 'admin';

  const TABS = [
    { key: 'customers', label: 'Customer Lookup', icon: Users },
    { key: 'servers', label: 'All Servers', icon: Server },
    ...(isAdmin ? [{ key: 'agents', label: 'Staff', icon: Shield }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <UserCheck className="w-4 h-4 text-blue-400" />
            </div>
            <h1 className="text-2xl font-black text-white">Staff Panel</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              isAdmin ? 'bg-purple-500/10 text-purple-400' : 'bg-blue-500/10 text-blue-400'
            }`}>
              {isAdmin ? 'Admin' : 'Support Agent'}
            </span>
          </div>
          <p className="text-gray-400 text-sm ml-11">Internal customer management tools</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setSelectedCustomer(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                tab === key ? 'bg-green-500 text-gray-950' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'customers' && (
          selectedCustomer
            ? <CustomerProfile customerId={selectedCustomer.id} onBack={() => setSelectedCustomer(null)} isAdmin={isAdmin} />
            : <CustomerSearch onSelect={setSelectedCustomer} />
        )}
        {tab === 'servers' && <ServerList />}
        {tab === 'agents' && isAdmin && <AgentsTab />}
      </div>
    </div>
  );
};

export default StaffPage;
